import { jsPDF } from 'jspdf';
import moment from 'moment';

export const printTransfer = async (transfer) => {
  try {
    // Initialize PDF document
    const doc = new jsPDF({ 
      orientation: 'portrait', 
      unit: 'mm', 
      format: 'a4',
      hotfixes: ['px_scaling']
    });

    // Validate transfer data
    if (!transfer || !transfer.items) {
      throw new Error('Invalid transfer data');
    }

    // Set default font
    doc.setFont('helvetica');

    // Header Section
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`TRANSFER #${transfer.transfer_number}`, 105, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Status: ${transfer.status}`, 105, 20, { align: 'center' });
    doc.text(`Created: ${moment(transfer.created_at).format('LLL')}`, 105, 25, { align: 'center' });
    doc.line(10, 30, 200, 30);

    // Transfer Details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text("Transfer Details", 14, 40);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`From: ${transfer.source_shop.name} (${transfer.source_shop.type})`, 14, 50);
    doc.text(`To: ${transfer.target_shop.name} (${transfer.target_shop.type})`, 14, 55);
    doc.text(`Created By: ${transfer.creator?.name || 'N/A'}`, 14, 60);
    
    if (transfer.approved_by) {
      doc.text(`Approved By: ${transfer.approver?.name || 'N/A'}`, 14, 65);
      doc.text(`Approved At: ${moment(transfer.approved_at).format('LLL')}`, 14, 70);
    }
    
    if (transfer.shipped_at) {
      doc.text(`Shipped At: ${moment(transfer.shipped_at).format('LLL')}`, 14, 75);
    }
    
    if (transfer.notes) {
      const splitNotes = doc.splitTextToSize(`Notes: ${transfer.notes}`, 180);
      doc.text(splitNotes, 14, 80);
    }

    // Items Table Setup - Added S/N column
    const tableColumns = [
      { title: "S/N", width: 10, align: 'center' }, // New serial number column
      { title: "Product", width: 55, align: 'left' }, // Reduced width to accommodate S/N
      { title: "Barcode", width: 30, align: 'left' },
      { title: "Requested", width: 25, align: 'right' },
      { title: "Shipped", width: 25, align: 'right' },
      { title: "Received", width: 25, align: 'right' }
    ];

    const startX = 14;
    let startY = 90;
    const rowHeight = 8;
    const pageHeight = doc.internal.pageSize.height;
    const marginBottom = 20;

    // Draw Table Header
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    let currentX = startX;
    tableColumns.forEach(column => {
      doc.text(column.title, currentX + (column.align === 'right' ? column.width - 3 : 
               column.align === 'center' ? column.width/2 : 3), startY, {
        align: column.align
      });
      currentX += column.width;
    });
    startY += rowHeight;

    // Draw Table Rows with Serial Numbers
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    transfer.items.forEach((item, index) => {
      // Check if we need a new page
      if (startY + rowHeight + marginBottom > pageHeight) {
        doc.addPage();
        startY = 30; // Reset Y position for new page
        
        // Redraw header on new page
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        currentX = startX;
        tableColumns.forEach(column => {
          doc.text(column.title, currentX + (column.align === 'right' ? column.width - 3 : 
                   column.align === 'center' ? column.width/2 : 3), startY, {
            align: column.align
          });
          currentX += column.width;
        });
        startY += rowHeight;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
      }

      // Draw row cells
      currentX = startX;
      
      // Serial Number (centered)
      doc.text(String(index + 1), currentX + tableColumns[0].width/2, startY, {
        align: 'center'
      });
      currentX += tableColumns[0].width;
      
      // Product Name (with wrapping)
      const productLines = doc.splitTextToSize(item.product?.name || 'N/A', tableColumns[1].width - 6);
      productLines.forEach((line, i) => {
        doc.text(line, currentX + 3, startY + (i * 4));
      });
      currentX += tableColumns[1].width;
      
      // Barcode
      doc.text(item.barcode || 'N/A', currentX + 3, startY);
      currentX += tableColumns[2].width;
      
      // Requested Quantity (right aligned)
      doc.text(String(item.quantity_requested || 0), currentX + tableColumns[3].width - 3, startY, {
        align: 'right'
      });
      currentX += tableColumns[3].width;
      
      // Shipped Quantity (right aligned)
      doc.text(String(item.quantity_shipped || '-'), currentX + tableColumns[4].width - 3, startY, {
        align: 'right'
      });
      currentX += tableColumns[4].width;
      
      // Received Quantity (right aligned)
      doc.text(String(item.quantity_received || '-'), currentX + tableColumns[5].width - 3, startY, {
        align: 'right'
      });
      
      // Calculate row height based on wrapped text
      const rowHeightMultiplier = productLines.length > 1 ? productLines.length : 1;
      startY += rowHeight * rowHeightMultiplier;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, 105, 287, { align: 'center' });
      doc.text(`Printed: ${moment().format('LLL')}`, 195, 287, { align: 'right' });
    }

    // Save the PDF
    doc.save(`transfer_${transfer.transfer_number}.pdf`);
    return true;
  } catch (error) {
    console.error('PDF generation error:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};